import { NavLink } from "react-router-dom";
import { ListChecks, LogOut, UserCircle2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

interface NavItem {
  to: string;
  label: string;
  icon: typeof ListChecks;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/applications", label: "Applications", icon: ListChecks },
];

interface SidebarProps {
  /** Called when a nav link is activated. On mobile the layout uses this
   *  to close the slide-in drawer after navigation. */
  onNavigate?: () => void;
  /** When provided, the header renders a close (X) button. Only passed in
   *  the mobile drawer; the static lg+ sidebar leaves it undefined. */
  onClose?: () => void;
}

export function Sidebar({ onNavigate, onClose }: SidebarProps = {}) {
  const { decoded, signout } = useAuth();

  return (
    <aside className="flex h-full w-64 shrink-0 flex-col border-r border-brand-border bg-brand-bg-deep">
      <div className="flex h-16 items-center justify-between border-b border-brand-border px-6">
        <span className="flex items-center gap-2.5 text-base font-semibold tracking-tight text-white">
          <span
            className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-brand-blue-bright to-brand-blue text-[11px] font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]"
            aria-hidden="true"
          >
            PP
          </span>
          Payment Products
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="-mr-2 rounded-md p-2 text-brand-muted hover:bg-brand-surface hover:text-brand-text"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </div>
      <nav className="flex-1 px-3 py-4">
        <ul className="space-y-1">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-brand-blue/15 text-white before:absolute before:inset-y-2 before:-left-3 before:w-0.5 before:rounded-full before:bg-brand-blue-bright"
                      : "text-brand-muted hover:bg-brand-surface hover:text-brand-text"
                  }`
                }
              >
                <item.icon className="h-4 w-4" aria-hidden="true" />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="border-t border-brand-border p-4">
        <div className="mb-3 flex items-center gap-2 text-sm">
          <UserCircle2
            className="h-4 w-4 text-brand-muted"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="truncate text-brand-text">{decoded?.email}</p>
            <p className="text-xs text-brand-muted">
              {decoded?.role
                ? decoded.role.charAt(0).toUpperCase() + decoded.role.slice(1)
                : ""}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={signout}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-brand-muted transition-colors hover:bg-brand-surface hover:text-brand-text"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
